import Icon from '@tiger/icons';
import { Input } from 'antd';
import { ReactNode, useEffect, useMemo, useRef } from 'react';
import { $Formutil, FormItem, withForm } from 'react-antd-formutil';
import errorImg from 'components/ErrorBox/banner.png';
import classlist from 'utils/classlist';
import { FieldSelectProps } from '..';

interface PickerViewProp extends FieldSelectProps {
    value: string | number;
    onChange: (value: string) => void;
    notFoundContent?: ReactNode;
    label?: string;
    $formutil: $Formutil<{ search: string }>;
}

const PickerView: React.FC<PickerViewProp> = ({
    onChange,
    onSearch,
    $formutil,
    data = [],
    label = __('请选择'),
    showSearch,
    value,
    searchPlaceholder,
    onSearchInputClick,
    dropdownHeader,
    notFoundContent
}) => {
    const bodyRef = useRef<HTMLDivElement | null>(null);
    const headerRef = useRef<HTMLDivElement | null>(null);
    const { $params } = $formutil;

    const confirm = value => {
        onChange(value);
    };

    useEffect(() => {
        const handleSearch = value => {
            if (onSearch) {
                onSearch(value);
            }
        };

        if ($params.search) {
            handleSearch($params.search);
        }
    }, [$params.search, onSearch]);

    useEffect(() => {
        const handleMove = e => {
            e.preventDefault();
        };

        const handleScroll = e => {
            e.stopPropagation();
        };

        headerRef.current?.addEventListener('touchmove', handleMove);
        bodyRef.current?.addEventListener('touchmove', handleScroll);

        return () => {
            headerRef.current?.removeEventListener('touchmove', handleMove);
            bodyRef.current?.removeEventListener('touchmove', handleScroll);
        };
    }, []);

    const list = useMemo(() => {
        if ($params.search && !onSearch) {
            return data.filter(t => new RegExp($params.search, 'i').test(t.label));
        }

        return data;
    }, [data, $params.search, onSearch]);

    return (
        <div className="field-select-picker-view">
            <div className="field-select-picker-header">
                <div className="header-wrap" ref={ref => (headerRef.current = ref)}>
                    <span className="header-title">{label}</span>
                </div>
                {showSearch && (
                    <FormItem className="picker-search" key="search" name="search">
                        <Input
                            prefix={<Icon type="search" />}
                            placeholder={searchPlaceholder}
                            onClick={onSearchInputClick}
                        />
                    </FormItem>
                )}
                <div className="sticky-top-content">{dropdownHeader}</div>
            </div>
            <div className="field-select-picker-body" ref={ref => (bodyRef.current = ref)}>
                {list?.length > 0 ? (
                    <div className="select-picker-list">
                        {list.map(item =>
                            item.extraLabel ? (
                                <div
                                    key={item.value}
                                    onClick={item.disabled ? undefined : () => confirm(item.value)}
                                    className={classlist('select-picker-option', 'extra-option', {
                                        disabled: item.disabled
                                    })}>
                                    <div className="extra-option-wrapper">{item.extraLabel}</div>
                                    {value === item.value && <Icon type="check" className="option-check" />}
                                </div>
                            ) : (
                                <div
                                    key={item.value}
                                    className={classlist('select-picker-option', { disabled: item.disabled })}
                                    onClick={item.disabled ? undefined : () => confirm(item.value)}>
                                    <div>
                                        <div className="option-content">
                                            {item.icon && <img className="select-option-icon" src={item.icon} alt="" />}
                                            {item.extraLabel || (
                                                <span dangerouslySetInnerHTML={{ __html: item.label }}></span>
                                            )}
                                        </div>
                                        {item.disabled && item.disabledDesc && (
                                            <p className="option-desc">{item.disabledDesc}</p>
                                        )}
                                    </div>
                                    {value === item.value && <Icon type="check" className="option-check" />}
                                </div>
                            )
                        )}
                    </div>
                ) : (
                    <p className="no-data">
                        <img className="no-data-img" src={errorImg} alt="" />
                        {notFoundContent || __('暂无数据')}
                    </p>
                )}
            </div>
        </div>
    );
};

export default withForm(PickerView);
